import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { get } from '@ember/helper';
import Chip from 'tio-ui/components/chip';
import { PhPlus, PhCheck } from 'ember-phosphor-icons';
import TooltipText from './tooltip-text';
import type { FieldOption } from '../../utils/opensearch-field-mapper';

// Map OpenSearch/report types to display labels and chip intents
const TYPE_CONFIG: Record<
  string,
  { label: string; intent: 'default' | 'warning' | 'success' | 'primary' | 'danger' }
> = {
  keyword: { label: 'text', intent: 'default' },
  text: { label: 'text', intent: 'default' },
  date: { label: 'date', intent: 'warning' },
  boolean: { label: 'boolean', intent: 'danger' },
  integer: { label: 'number', intent: 'success' },
  long: { label: 'number', intent: 'success' },
  float: { label: 'number', intent: 'success' },
  double: { label: 'number', intent: 'success' },
  short: { label: 'number', intent: 'success' },
  byte: { label: 'number', intent: 'success' },
};

function getTypeConfig(type: string) {
  return TYPE_CONFIG[type] || { label: type, intent: 'default' as const };
}

export interface AddMenuItem {
  label: string;
  onSelect: (field: FieldOption) => void;
  /** When provided, the item is only offered for fields it returns true for. */
  isAvailable?: (field: FieldOption) => boolean;
}

export interface FieldListSignature {
  Args: {
    groupedFields: Array<[string, FieldOption[]]>;
    selectedKeys: string[];
    onAdd: (field: FieldOption) => void;
    onRemove: (field: FieldOption) => void;
    isLoading?: boolean;
    addMenuItems?: AddMenuItem[];
    /**
     * Keep the add-menu available for already-selected fields (instead of
     * click-to-remove) — used by the dashboard widget rail so a selected field
     * can be re-assigned to another role or removed via the menu.
     */
    menuForSelected?: boolean;
  };
}

class FieldList extends Component<FieldListSignature> {
  @tracked collapsedGroups = new Set<string>();
  @tracked activeMenuKey: string | null = null;

  get selectedSet() {
    return new Set(this.args.selectedKeys);
  }

  isFieldSelected = (key: string) => {
    return this.selectedSet.has(key);
  };

  isGroupCollapsed = (groupName: string) => {
    return this.collapsedGroups.has(groupName);
  };

  isMenuOpen = (key: string) => {
    return this.activeMenuKey === key;
  };

  toggleGroup = (groupName: string) => {
    const next = new Set(this.collapsedGroups);
    if (next.has(groupName)) {
      next.delete(groupName);
    } else {
      next.add(groupName);
    }
    this.collapsedGroups = next;
  };

  menuItemsFor = (field: FieldOption): AddMenuItem[] =>
    (this.args.addMenuItems ?? []).filter((mi) => mi.isAvailable?.(field) ?? true);

  handleFieldClick = (field: FieldOption) => {
    if (this.selectedSet.has(field.key) && !this.args.menuForSelected) {
      this.args.onRemove(field);
    } else if (this.args.addMenuItems) {
      // Show menu instead of directly adding
      this.activeMenuKey = this.activeMenuKey === field.key ? null : field.key;
    } else {
      this.args.onAdd(field);
    }
  };

  handleMenuSelect = (menuItem: AddMenuItem, field: FieldOption) => {
    menuItem.onSelect(field);
    this.activeMenuKey = null;
  };

  dismissMenu = () => {
    this.activeMenuKey = null;
  };

  <template>
    <div class="pb-1" data-test-field-list>
      {{#if @isLoading}}
        <div class="px-3 py-4 text-sm tio-fg-muted text-center" data-test-field-list-loading>
          Loading fields...
        </div>
      {{else if (eq @groupedFields.length 0)}}
        <div class="px-3 py-4 text-sm tio-fg-muted text-center">
          No fields available
        </div>
      {{else}}
        {{#each @groupedFields as |group|}}
          {{#let (get group "0") (get group "1") as |groupName fields|}}
            <div class="border-t tio-border-subtle first:border-t-0">
              <button
                type="button"
                class="w-full tio-row-extra-tight px-3 py-1.5 tio-text-xs-medium tio-fg-muted hover:tio-bg-sunken uppercase tracking-wide"
                {{on "click" (fn this.toggleGroup groupName)}}
              >
                <span class="tio-text-micro">
                  {{#if (this.isGroupCollapsed groupName)}}▶{{else}}▼{{/if}}
                </span>
                {{groupName}}
              </button>

              {{#unless (this.isGroupCollapsed groupName)}}
                <div>
                  {{#each fields as |field|}}
                    {{#let (getTypeConfig field.type) as |typeConfig|}}
                      <button
                        type="button"
                        class="w-full tio-row-between px-3 py-1.5 text-sm hover:tio-bg-sunken group
                          {{if
                            (this.isFieldSelected field.key)
                            'tio-bg-info-soft tio-fg-info-h'
                            'tio-fg-secondary'
                          }}"
                        data-test-field-item={{field.key}}
                        {{on "click" (fn this.handleFieldClick field)}}
                      >
                        <TooltipText @label={{field.label}} class="truncate flex-1 text-left">
                          {{field.label}}
                        </TooltipText>
                        <span class="tio-row-extra-tight shrink-0 ml-1">
                          <Chip @size="sm" @intent={{typeConfig.intent}} @appearance="faded">
                            {{typeConfig.label}}
                          </Chip>
                          {{#if (this.isFieldSelected field.key)}}
                            <PhCheck @size={{14}} class="tio-fg-info" />
                          {{else}}
                            <PhPlus
                              @size={{14}}
                              class="tio-fg-muted group-hover:tio-fg-secondary"
                            />
                          {{/if}}
                        </span>
                      </button>
                      {{#if (this.isMenuOpen field.key)}}
                        <div class="relative">
                          <div
                            class="absolute right-2 z-20 w-40 tio-bg-surface border tio-border-default rounded-md shadow-lg py-1"
                          >
                            {{#each (this.menuItemsFor field) as |menuItem|}}
                              <button
                                type="button"
                                class="w-full text-left px-3 py-1.5 text-xs tio-fg-secondary hover:tio-bg-brand-soft hover:tio-fg-brand"
                                {{on "click" (fn this.handleMenuSelect menuItem field)}}
                              >
                                {{menuItem.label}}
                              </button>
                            {{/each}}
                            <button
                              type="button"
                              class="w-full text-left px-3 py-1.5 text-xs tio-fg-muted hover:tio-fg-secondary border-t tio-border-subtle"
                              {{on "click" this.dismissMenu}}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      {{/if}}
                    {{/let}}
                  {{/each}}
                </div>
              {{/unless}}
            </div>
          {{/let}}
        {{/each}}
      {{/if}}
    </div>
  </template>
}

export { getTypeConfig };
export default FieldList;
